import {
  type CertificateInstance,
  type CertificateInstanceCoordinator,
  setCertificateInstances,
  type queryKboResponse,
} from '../../../Store/DataSlice'
import uuid4 from 'uuid4'
import './CertificateInstanceAdd.scss'
import { useEffect, useMemo, useState } from 'react'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useLocalization } from '../../../Hooks/useLocalization'
import {
  getSimplifiedVatNumber,
  isBelgianVat,
  isValidEmail,
  isValidTelephone,
  isValidVatNumber,
  isValidWebsite,
} from '../../../Utils/Validations'
import {
  CountryCodes,
  emailPlaceholder,
  telephonePlaceholder,
  websitePlaceholder,
} from '../../../Utils/Constants'
import { Button } from '../../../Components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'
import { type RootState } from '../../../Store/Store'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'
import { LoadingSpinner } from '../../../Components/LoadingSpinner/LoadingSpinner'

let latestFetchId: string | undefined
const emptyCertificateInstance: CertificateInstance = {
  vatNumber: '',
  name: '',
  email: '',
  website: '',
  telephone: '',
  addressLine1: '',
  postalCode: '',
  city: '',
  countryCode: 'BE',
  coordinators: [],
}
const emptyCertificateInstanceCoordinatorInstance: CertificateInstanceCoordinator =
  {
    uuid: '',
    name: '',
    email: '',
    telephone: '',
  }

export function CertificateInstanceAdd() {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate-instance')
  const countryLocalization = useLocalization('country')

  const [certificateInstance, setCertificateInstance] =
    useState<CertificateInstance>({
      ...emptyCertificateInstance,
    })
  const [coordinatorToAdd, setCoordinatorToAdd] =
    useState<CertificateInstanceCoordinator>(
      emptyCertificateInstanceCoordinatorInstance
    )
  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  const [vatNumberIsValid, setVatNumberIsValid] = useState(false)
  const [isFetchDataInProgress, setIsFetchDataInProgress] = useState(false)

  const isBelgianVatNumber = useMemo(
    () => isBelgianVat(certificateInstance.vatNumber),
    [certificateInstance]
  )

  const onVatNumberChange = (newVatNumber: string) => {
    const simplifiedVatNumber = getSimplifiedVatNumber(newVatNumber)
    const isValid =
      simplifiedVatNumber?.length > 2 && isValidVatNumber(simplifiedVatNumber)
    setVatNumberIsValid(isValid)
    const certificateInstanceUpdates: Partial<CertificateInstance> = {
      vatNumber: simplifiedVatNumber,
    }
    if (isValid) {
      if (
        allCertificateInstances.some((c) => c.vatNumber === simplifiedVatNumber)
      ) {
        dispatch(
          setDestinationView({
            destinationView: 'certificate-instance-edit',
            detailKey: simplifiedVatNumber,
          })
        )
        return
      }
      const countryCode = simplifiedVatNumber.substring(0, 2).toUpperCase()
      if (countryCode === 'BE') {
        setIsFetchDataInProgress(true)
        const fetchId = uuid4()
        latestFetchId = fetchId
        fetch(
          `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/query-kruispuntbank?vatNumber=${simplifiedVatNumber}`,
          { headers: { ...getAuthorizationHeader() } }
        )
          .then(async (response) => await response.json())
          .then((response: queryKboResponse) => {
            if (fetchId === latestFetchId) {
              if (response.success) {
                setCertificateInstance((p) => ({
                  ...p,
                  vatNumber: response.lookupData.vatNumber,
                  name: response.lookupData.name,
                  addressLine1: response.lookupData.addressLine1,
                  postalCode: response.lookupData.postalCode,
                  city: response.lookupData.city,
                  countryCode: response.lookupData.countryCode,
                }))
              } else {
                console.warn(
                  `KBO lookup failed for ${simplifiedVatNumber}`,
                  response.error
                )
                alert(
                  `KBO lookup failed for ${simplifiedVatNumber}: ${response.error}`
                )
              }
              setIsFetchDataInProgress(false)
            } else {
              console.log(`Dropping outdated fetch result: `, response)
            }
          })
          .catch((err) => {
            console.warn(`fetch query-kruispuntbank failed: `, err)
          })
      } else {
        if (CountryCodes.includes(countryCode)) {
          certificateInstanceUpdates.countryCode = countryCode
        }
      }
    } else {
      latestFetchId = undefined
      setIsFetchDataInProgress(false)
    }
    setCertificateInstance((p) => ({ ...p, ...certificateInstanceUpdates }))
  }

  const [validationErrors, setValidationErrors] = useState<validationError[]>(
    []
  )
  useEffect(() => {
    const newValidationErrors: validationError[] = []

    if (!certificateInstance.vatNumber)
      newValidationErrors.push({ property: 'vatNumber', type: 'isRequired' })

    if (!certificateInstance.name)
      newValidationErrors.push({ property: 'name', type: 'isRequired' })

    if (!certificateInstance.countryCode)
      newValidationErrors.push({
        property: 'countryCode',
        type: 'isRequired',
      })

    if (certificateInstance.email && !isValidEmail(certificateInstance.email))
      newValidationErrors.push({ property: 'email', type: 'format' })
    if (
      certificateInstance.website &&
      !isValidWebsite(certificateInstance.website)
    )
      newValidationErrors.push({ property: 'website', type: 'format' })
    if (
      certificateInstance.telephone &&
      !isValidTelephone(certificateInstance.telephone)
    )
      newValidationErrors.push({ property: 'telephone', type: 'format' })

    if (certificateInstance.coordinators.length === 0)
      newValidationErrors.push({ property: 'coordinators', type: 'isRequired' })
    setValidationErrors(newValidationErrors)
  }, [certificateInstance])

  const [coordinatorValidationErrors, setCoordinatorValidationErrors] =
    useState<validationError[]>([])
  useEffect(() => {
    const newValidationErrors: validationError[] = []

    if (!coordinatorToAdd.name)
      newValidationErrors.push({ property: 'name', type: 'isRequired' })

    if (!coordinatorToAdd.email)
      newValidationErrors.push({ property: 'email', type: 'isRequired' })

    if (coordinatorToAdd.email && !isValidEmail(coordinatorToAdd.email))
      newValidationErrors.push({ property: 'email', type: 'format' })

    if (!coordinatorToAdd.telephone)
      newValidationErrors.push({ property: 'telephone', type: 'isRequired' })

    if (
      coordinatorToAdd.telephone &&
      !isValidTelephone(coordinatorToAdd.telephone)
    )
      newValidationErrors.push({ property: 'telephone', type: 'format' })
    setCoordinatorValidationErrors(newValidationErrors)
  }, [coordinatorToAdd])

  const [saveInProgress, setSaveInProgress] = useState(false)
  const saveCertificateInstance = () => {
    setSaveInProgress(true)

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-instance-add`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificateInstance,
        }),
      }
    )
      .then(() => {
        dispatch(
          setCertificateInstances([
            ...allCertificateInstances,
            certificateInstance,
          ])
        )
        setTimeout(() => {
          dispatch(
            setDestinationView({ destinationView: 'certificate-instance-list' })
          )
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-instance-add failed: `, err)
      })
  }

  return (
    <div className="certificate-instance-add">
      {saveInProgress ? (
        <div className="save-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'certificate-instance-save-in-progress',
            'De certificaat instelling word momenteel opgeslagen.'
          )}
        </div>
      ) : isFetchDataInProgress ? (
        <div className="vat-fetch-in-progress">
          <LoadingSpinner color="dark" />
          {localization.text(
            'vat-info-fetch-in-progress',
            'Gegevens voor onderneming {vatNumber} worden opgehaald',
            { vatNumber: certificateInstance.vatNumber }
          )}
        </div>
      ) : (
        <>
          <TextInput
            label={localization.text('vat-number', 'Ondernemingsnummer')}
            placeholder="BE0999999999"
            onChange={onVatNumberChange}
            isValid={vatNumberIsValid}
            isReadOnly={saveInProgress}
            value={certificateInstance.vatNumber}
            helperText={
              !vatNumberIsValid
                ? localization.text(
                    'invalid-vat-number',
                    'Geef een geldig vat nummer startende met de landcode'
                  )
                : undefined
            }
          />

          <TextInput
            label={localization.text('name', 'Naam')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                name: newValue,
              }))
            }}
            value={certificateInstance.name}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'name')
            }
            helperText={
              vatNumberIsValid
                ? validationErrors.find((ve) => ve.property === 'name')
                    ?.type === 'isRequired'
                  ? localization.text(
                      'name-missing',
                      'Geef een geldige naam in'
                    )
                  : undefined
                : undefined
            }
          />
          <TextInput
            label={localization.text('email', 'Email')}
            isReadOnly={!vatNumberIsValid || saveInProgress}
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                email: newValue,
              }))
            }}
            value={certificateInstance.email}
            placeholder={emailPlaceholder}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'email')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'email')?.type ===
              'format'
                ? localization.text(
                    'email-invalid',
                    'Email formaat is incorrect'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('website', 'Website')}
            isReadOnly={!vatNumberIsValid || saveInProgress}
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                website: newValue,
              }))
            }}
            value={certificateInstance.website}
            placeholder={websitePlaceholder}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'website')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'website')?.type ===
              'format'
                ? localization.text(
                    'website-invalid',
                    'Website formaat is incorrect'
                  )
                : undefined
            }
          />
          <TextInput
            label={localization.text('telephone', 'Telefoon')}
            isReadOnly={!vatNumberIsValid || saveInProgress}
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                telephone: newValue,
              }))
            }}
            value={certificateInstance.telephone}
            placeholder={telephonePlaceholder}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'telephone')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'telephone')
                ?.type === 'format'
                ? localization.text(
                    'telephone-invalid',
                    'Telefoon formaat is incorrect'
                  )
                : undefined
            }
          />

          <TextInput
            label={localization.text('addressLine1', 'Straat en huisnummer')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                addressLine1: newValue,
              }))
            }}
            value={certificateInstance.addressLine1}
          />
          <TextInput
            label={localization.text('postalCode', 'Postcode')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                postalCode: newValue,
              }))
            }}
            value={certificateInstance.postalCode}
          />
          <TextInput
            label={localization.text('city', 'Gemeente')}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                city: newValue,
              }))
            }}
            value={certificateInstance.city}
          />
          <SingleSelect
            label={localization.text('country', 'Land')}
            items={CountryCodes.map((code) => ({
              value: code,
              label: code ? countryLocalization.text(code, code) : '',
            }))}
            isReadOnly={
              !vatNumberIsValid || isBelgianVatNumber || saveInProgress
            }
            onChange={(newValue) => {
              setCertificateInstance((previous) => ({
                ...previous,
                countryCode: newValue,
              }))
            }}
            value={certificateInstance.countryCode}
            isValid={
              !vatNumberIsValid ||
              !validationErrors.some((ve) => ve.property === 'countryCode')
            }
            helperText={
              validationErrors.find((ve) => ve.property === 'countryCode')
                ?.type === 'isRequired'
                ? localization.text(
                    'country-code-missing',
                    'Kies een geldig land'
                  )
                : undefined
            }
          />

          <div className="coordinators">
            <div className="label">
              {localization.text('coordinators', 'VCA coordinators')}
            </div>
            {vatNumberIsValid ? (
              <>
                {validationErrors.find((ve) => ve.property === 'coordinators')
                  ?.type === 'isRequired' ? (
                  <div className="custom-helper-text">
                    {localization.text(
                      'coordinators-missing',
                      'Minstens één coordinator is verplicht'
                    )}
                  </div>
                ) : null}
                {certificateInstance.coordinators.map((coordinator) => (
                  <div key={coordinator.uuid} className="coordinator-option">
                    <div className="coordinator-data">
                      <span className="name">{coordinator.name}</span>
                      <span className="email">
                        <span className="label">
                          {localization.text('coordinator-email', 'Email')}:{' '}
                        </span>
                        {coordinator.email}
                      </span>
                      <span className="telephone">
                        <span className="label">
                          {localization.text('coordinator-telephone', 'GSM')}:{' '}
                        </span>
                        {coordinator.telephone}
                      </span>
                    </div>
                    <Button
                      importance={'secondary'}
                      text={localization.text(
                        'remove-coordinator',
                        'Verwijder coordinator'
                      )}
                      size="small"
                      onClick={() => {
                        setCertificateInstance((previous) => ({
                          ...previous,
                          coordinators: previous.coordinators.filter(
                            (b) => b.uuid !== coordinator.uuid
                          ),
                        }))
                      }}
                    />
                  </div>
                ))}
                <div className="add-coordinator">
                  <h3>
                    {localization.text(
                      'add-coordinator',
                      'Voeg VCA coordinator toe'
                    )}
                  </h3>
                  <TextInput
                    label={localization.text('coordinator-name', 'Naam')}
                    isReadOnly={saveInProgress}
                    onChange={(newValue) => {
                      setCoordinatorToAdd((p) => ({ ...p, name: newValue }))
                    }}
                    value={coordinatorToAdd.name}
                    isValid={
                      !coordinatorValidationErrors.some(
                        (ve) => ve.property === 'name'
                      )
                    }
                    helperText={
                      coordinatorValidationErrors.find(
                        (ve) => ve.property === 'name'
                      )?.type === 'isRequired'
                        ? localization.text(
                            'name-missing',
                            'Geef een geldige naam in'
                          )
                        : undefined
                    }
                  />
                  <TextInput
                    label={localization.text('coordinator-email', 'Email')}
                    isReadOnly={saveInProgress}
                    onChange={(newValue) => {
                      setCoordinatorToAdd((p) => ({ ...p, email: newValue }))
                    }}
                    value={coordinatorToAdd.email}
                    placeholder={emailPlaceholder}
                    isValid={
                      !coordinatorValidationErrors.some(
                        (ve) => ve.property === 'email'
                      )
                    }
                    helperText={
                      coordinatorValidationErrors.find(
                        (ve) => ve.property === 'email'
                      )?.type === 'isRequired'
                        ? localization.text(
                            'email-missing',
                            'Geef een geldig email address in'
                          )
                        : coordinatorValidationErrors.find(
                            (ve) => ve.property === 'email'
                          )?.type === 'format'
                        ? localization.text(
                            'email-invalid',
                            'Email formaat is incorrect'
                          )
                        : undefined
                    }
                  />
                  <TextInput
                    label={localization.text('coordinator-telephone', 'GSM')}
                    isReadOnly={saveInProgress}
                    onChange={(newValue) => {
                      setCoordinatorToAdd((p) => ({
                        ...p,
                        telephone: newValue,
                      }))
                    }}
                    value={coordinatorToAdd.telephone}
                    placeholder={telephonePlaceholder}
                    isValid={
                      !coordinatorValidationErrors.some(
                        (ve) => ve.property === 'telephone'
                      )
                    }
                    helperText={
                      coordinatorValidationErrors.find(
                        (ve) => ve.property === 'telephone'
                      )?.type === 'isRequired'
                        ? localization.text(
                            'telephone-required',
                            'GSM is verplict'
                          )
                        : coordinatorValidationErrors.find(
                            (ve) => ve.property === 'telephone'
                          )?.type === 'format'
                        ? localization.text(
                            'telephone-invalid',
                            'Telefoon formaat is incorrect'
                          )
                        : undefined
                    }
                  />
                  <Button
                    importance={'primary'}
                    text={localization.text(
                      'add-coordinator',
                      'Voeg VCA coordinator toe'
                    )}
                    onClick={() => {
                      setCertificateInstance((previous) => ({
                        ...previous,
                        coordinators: [
                          ...(previous.coordinators ??
                            certificateInstance.coordinators ??
                            []),
                          {
                            ...coordinatorToAdd,
                            uuid: uuid4(),
                          },
                        ],
                      }))
                      setCoordinatorToAdd({
                        ...emptyCertificateInstanceCoordinatorInstance,
                      })
                    }}
                    disabled={
                      coordinatorValidationErrors.length > 0 || saveInProgress
                    }
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="buttons">
            <Button
              importance={'primary'}
              text={localization.text(
                'safe-add-button',
                'Voeg certificaat instantie toe'
              )}
              onClick={saveCertificateInstance}
              disabled={
                !vatNumberIsValid ||
                validationErrors.length > 0 ||
                saveInProgress
              }
            />
          </div>
        </>
      )}
    </div>
  )
}

interface validationError {
  property: string
  type: 'isRequired' | 'format'
}
